import React, { useMemo } from 'react'
import styled, { type DefaultTheme, useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FaExclamationTriangle, FaInfoCircle, FaPlug, FaSlack } from 'react-icons/fa'
import { Spinner } from '@components/ui/Primitives'
import { useBusinessContext } from '@root/context'
import { type IMcpSession, type McpConnectionStatus, useWidgetSession } from '@query'
import kraviaLogo from '@assets/kravia/kravia-logo.jpeg'

type ProviderVisual = { color: string; icon: React.ReactNode; isLogo?: boolean }

// Provider visual identity. Curated connectors (Kravia) carry their real brand logo + colour;
// everything else derives from theme colours (no external brand hexes).
const providerVisual = (templateKey: string, theme: DefaultTheme): ProviderVisual => {
  switch (templateKey) {
    case 'slack':
      return { color: theme.luca.deepBlue, icon: <FaSlack /> }
    case 'kravia':
      return {
        color: theme.colors.kraviaOrange,
        icon: <img src={kraviaLogo} alt="" />,
        isLogo: true
      }
    default:
      return { color: theme.colors.iconGrayDark, icon: <FaPlug /> }
  }
}

export const ProviderAvatar: React.FC<{ templateKey: string; size?: number }> = ({
  templateKey,
  size = 2.4
}) => {
  const theme = useTheme()
  const { color, icon, isLogo } = providerVisual(templateKey, theme)
  return (
    <Avatar $color={color} $size={size} $isLogo={isLogo}>
      {icon}
    </Avatar>
  )
}

const Avatar = styled.div<{ $color: string; $size: number; $isLogo?: boolean }>`
  flex: 0 0 auto;
  width: ${({ $size }) => $size}rem;
  height: ${({ $size }) => $size}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color};
  /* A logo carries its own artwork edge-to-edge over the brand colour; icons get a soft tint. */
  background: ${({ $color, $isLogo }) =>
    $isLogo ? $color : `color-mix(in srgb, ${$color} 12%, white)`};
  box-shadow: ${({ $color, $isLogo }) =>
    $isLogo ? 'none' : `inset 0 0 0 1px color-mix(in srgb, ${$color} 22%, white)`};
  font-size: ${({ $size }) => $size * 0.45}rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const STATUS_TONE: Record<McpConnectionStatus, 'success' | 'info' | 'warning' | 'error'> = {
  connected: 'success',
  pending: 'info',
  needs_reauth: 'warning',
  error: 'error'
}

export const StatusPill: React.FC<{ status: McpConnectionStatus }> = ({ status }) => {
  const [t] = useTranslation()
  const tone = STATUS_TONE[status]
  return (
    <Pill className={tone}>
      <Dot className={tone} />
      {t(`lucaSettings.status.${status}`)}
    </Pill>
  )
}

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  white-space: nowrap;
  ${({ theme }) => `
    &.success { background: color-mix(in srgb, ${theme.colors.success} 14%, white); color: ${theme.colors.success}; }
    &.info { background: color-mix(in srgb, ${theme.colors.primary} 14%, white); color: ${theme.colors.primary}; }
    &.warning { background: color-mix(in srgb, ${theme.colors.warning} 18%, white); color: ${theme.colors.warning}; }
    &.error { background: color-mix(in srgb, ${theme.colors.error} 12%, white); color: ${theme.colors.error}; }
  `}
`

const Dot = styled.span`
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: currentColor;
`

export const WarningBanner: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children
}) => (
  <Banner>
    <FaExclamationTriangle />
    <div>
      <BannerTitle>{title}</BannerTitle>
      <BannerBody>{children}</BannerBody>
    </div>
  </Banner>
)

const Banner = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => `color-mix(in srgb, ${theme.colors.warning} 12%, white)`};
  border: 1px solid ${({ theme }) => `color-mix(in srgb, ${theme.colors.warning} 40%, white)`};
  color: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  svg {
    color: ${({ theme }) => theme.colors.warning};
    flex: 0 0 auto;
    margin-top: 0.1rem;
  }
`

const BannerTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

const BannerBody = styled.div`
  line-height: 1.4;
`

// Shown before the Luca widget session is ready, so the page never renders an empty
// profile / "no connections" that reads as broken while the session is still minting.
export const SessionGate: React.FC<{ loading: boolean; hasBusiness: boolean }> = ({
  loading,
  hasBusiness
}) => {
  const [t] = useTranslation()
  return (
    <Notice>
      {loading ? (
        <>
          <Spinner size={24} />
          <NoticeText>{t('lucaSettings.session.loading')}</NoticeText>
        </>
      ) : (
        <>
          <FaInfoCircle size={18} />
          <NoticeText>
            {hasBusiness ? t('lucaSettings.session.error') : t('lucaSettings.session.noBusiness')}
          </NoticeText>
        </>
      )}
    </Notice>
  )
}

const Notice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.xxl}rem ${({ theme }) => theme.spacing.lg}rem;
  color: ${({ theme }) => theme.colors.iconGrayDark};
`

const NoticeText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  max-width: 28rem;
`

export const LucaPageContent = styled.div`
  max-width: 60rem;
  container-type: inline-size;
`

export const useLucaWidgetSession = () => {
  const { slug } = useBusinessContext()
  const { data: widgetSession, isLoading: sessionLoading } = useWidgetSession(Boolean(slug), slug)
  const session = useMemo<IMcpSession>(
    () => ({ token: widgetSession?.token ?? null, country: widgetSession?.country ?? 'FI' }),
    [widgetSession?.token, widgetSession?.country]
  )

  return {
    slug,
    session,
    sessionLoading,
    ready: Boolean(slug && session.token)
  }
}
